import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { BookOpenCheck, Plus } from 'lucide-react';
import { api } from '@/lib/api-client';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useAuth } from '@/features/auth/AuthContext';

interface DashboardStats {
  totalSkills: number;
}

async function fetchStats(): Promise<DashboardStats> {
  const s = await api.get<{ total: number }>('/skills', { params: { page: 1, pageSize: 1 } });
  return { totalSkills: s.data.total };
}

export default function DashboardPage() {
  const { user } = useAuth();
  const stats = useQuery({ queryKey: ['dashboard-stats'], queryFn: fetchStats });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Olá, {user?.displayName || user?.email}</h1>
        <p className="text-sm text-text-muted">Visão geral do hub de conhecimento.</p>
      </header>

      {stats.isLoading ? (
        <div className="flex justify-center text-text-muted">
          <Spinner />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatCard icon={<BookOpenCheck size={20} />} label="Skills" value={stats.data?.totalSkills ?? 0} />
        </div>
      )}

      <Card>
        <CardBody className="flex flex-wrap gap-3">
          <Link to="/skills">
            <Button variant="secondary">
              <BookOpenCheck size={16} /> Ver skills
            </Button>
          </Link>
          {(user?.role === 'admin' || user?.role === 'editor') && (
            <Link to="/skills/nova">
              <Button>
                <Plus size={16} /> Nova skill
              </Button>
            </Link>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <Card>
      <CardBody>
        <div className="flex items-center justify-between mb-2 text-text-muted">
          <span className="text-xs uppercase tracking-wide">{label}</span>
          {icon}
        </div>
        <p className="text-3xl font-bold">{value}</p>
      </CardBody>
    </Card>
  );
}
